import { Head, router } from "@inertiajs/react"
import { Users, Search, Mail, UserPlus, Trash2, Pencil, Share2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { CoachLayout } from "../components/CoachLayout"
import { SectionTabs } from "../components/SectionTabs"
import { CoachGroupSwitcher } from "../components/CoachGroupSwitcher"
import { ShareableLinkCard } from "../components/ShareableLinkCard"
import { AddMemberDialog } from "../components/AddMemberDialog"
import { EditMemberDialog } from "../components/EditMemberDialog"
import { CoachRemoveUserDialog } from "../components/CoachRemoveUserDialog"
import { MemberStoryInviteCell } from "@/components/member-story/MemberStoryInviteCell"
import { MemberStoryInviteDialog } from "@/components/member-story/MemberStoryInviteDialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

const userManagementTabs = [
  { label: "Users", href: "/app/coach/users" },
  { label: "Groups", href: "/app/coach/group" },
  { label: "Prospects", href: "/app/coach/prospects" },
]

function StatusBadge({ status }) {
  if (status === "active") {
    return <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200">Active</Badge>
  }
  if (status === "pending" || status === "invited") {
    return <Badge variant="secondary" className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200">Invited</Badge>
  }
  if (status === "deactivated") {
    return <Badge variant="secondary" className="bg-zinc-100 text-zinc-700 hover:bg-zinc-100">Deactivated</Badge>
  }
  return <Badge variant="outline">{status}</Badge>
}

/** Build payload for EditMemberDialog (`id` = group_membership id). */
function coachUserToEditMember(u) {
  if (!u?.primaryMembershipId) return null
  return {
    id: u.primaryMembershipId,
    accountId: u.id,
    name: u.name,
    rawName: u.rawName ?? "",
    email: u.email,
    jobTitle: u.jobTitle,
    company: u.company,
    bio: u.bio,
    namePhonetic: u.namePhonetic,
    city: u.city,
    state: u.state,
    country: u.country,
  }
}

export default function CoachUsersIndex({
  currentUser,
  users = [],
  searchPool = [],
  coachGroup,
  coachGroups = [],
  memberStoryLinkUrl = null,
  coachDisplayName = "",
  flash = {},
}) {

  // Member Stories are in beta. The server already withholds the link and the
  // per-row invite data for a non-beta coach, and refuses the invite POST; this
  // hides the three entry points so they never see a door that is locked.
  const showMemberStories = !!currentUser?.isBetaTester

  // The invite flash carries the freshly minted link; showing it is the whole
  // point of the action, since nothing was emailed.
  const generatedStoryInvite = flash?.generated_member_story_invite || null
  const [storyInviteOpen, setStoryInviteOpen] = useState(false)
  useEffect(() => {
    if (generatedStoryInvite) setStoryInviteOpen(true)
  }, [generatedStoryInvite])
  const [search, setSearch] = useState("")
  const [addOpen, setAddOpen] = useState(false)
  const [pendingEdit, setPendingEdit] = useState(null)
  const [pendingRemoveUser, setPendingRemoveUser] = useState(null)

  const filtered = useMemo(() => {
    if (!search) return users
    const q = search.toLowerCase()
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      u.groups?.some((g) => g.name.toLowerCase().includes(q))
    )
  }, [search, users])

  const visibleTabs = userManagementTabs

  const handleRemoveMembership = (membershipId) => {
    router.delete(`/app/coach/group/members/${membershipId}`, {
      onFinish: () => setPendingRemoveUser(null),
    })
  }

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title="Users - Kingdom Factor" />

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Users</h1>
            <p className="text-zinc-600">
              Members of{" "}
              {coachGroups?.length >= 1 ? (
                <CoachGroupSwitcher coachGroup={coachGroup} coachGroups={coachGroups} />
              ) : (
                <span className="font-medium">{coachGroup?.name || "your group"}</span>
              )}
              .
            </p>
          </div>
          <Button variant="gradient" onClick={() => setAddOpen(true)}>
            <UserPlus className="h-4 w-4 mr-1.5" />
            Add Member
          </Button>
        </div>

        <SectionTabs tabs={visibleTabs} />

        {showMemberStories && (
          <ShareableLinkCard
            icon={Share2}
            title="Your story link"
            description="Drop this in a group chat instead of inviting members one at a time. Anyone you coach lands straight in their own interview."
            url={memberStoryLinkUrl}
            copyLabel="Copy story link"
          />
        )}

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            type="search"
            placeholder="Search by name, email, or group..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-zinc-500">
              <Users className="h-10 w-10 mx-auto mb-3 text-zinc-300" />
              <p className="font-medium">
                {search
                  ? "No matches"
                  : `No members in ${coachGroup?.name || "this group"} yet`}
              </p>
              <p className="text-sm">
                {search
                  ? "Try a different search term."
                  : "Members will appear here once they join, or use Add Member above."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <div className="rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-zinc-500">Name</TableHead>
                    <TableHead className="text-zinc-500 hidden md:table-cell">Email</TableHead>
                    <TableHead className="text-zinc-500 hidden sm:table-cell">Groups</TableHead>
                    <TableHead className="text-zinc-500 hidden sm:table-cell">Status</TableHead>
                    {showMemberStories && (
                      <TableHead className="text-zinc-500 hidden md:table-cell text-right w-36">Member story</TableHead>
                    )}
                    <TableHead className="text-zinc-500 text-right w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((u) => {
                    const editMember = coachUserToEditMember(u)
                    const canEdit = !!editMember
                    const canRemove = (u.groups?.length ?? 0) > 0
                    const detailHref = `/app/coach/members/${u.id}?back=users`
                    const navigate = () => router.visit(detailHref)
                    return (
                      <TableRow
                        key={u.id}
                        className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        tabIndex={0}
                        aria-label={`View ${u.name || "member"}`}
                        onClick={navigate}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault()
                            navigate()
                          }
                        }}
                      >
                        <TableCell>
                          <div className="font-medium text-zinc-900">{u.name || "—"}</div>
                          <div className="text-xs text-zinc-500 md:hidden flex items-center gap-1 mt-0.5">
                            <Mail className="h-3 w-3 shrink-0" />
                            {u.email}
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-zinc-700">{u.email}</TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {u.groups?.length > 0 ? (
                              u.groups.map((g) => (
                                <Badge key={g.membershipId} variant="outline" className="text-xs">
                                  {g.name}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-xs text-zinc-400">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <StatusBadge status={u.status} />
                        </TableCell>
                        {showMemberStories && (
                          <TableCell
                            className="hidden md:table-cell text-right p-2"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              // Same reason as the Actions cell below: keep
                              // Enter/Space on a button from navigating the row.
                              if (e.key === "Enter" || e.key === " ") e.stopPropagation()
                            }}
                          >
                            <MemberStoryInviteCell user={u} />
                          </TableCell>
                        )}
                        <TableCell className="text-right p-2">
                          <div
                            className="flex justify-end gap-1"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              // Without this, Enter/Space on a focused
                              // action button bubbles to the row's
                              // onKeyDown which preventDefault()'s the
                              // button's native activation and navigates
                              // instead -- breaking the action for
                              // keyboard users.
                              if (e.key === "Enter" || e.key === " ") e.stopPropagation()
                            }}
                          >
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-zinc-500 hover:text-kf-red"
                                title="Edit profile"
                                onClick={() => setPendingEdit(editMember)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            {canRemove ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-zinc-500 hover:text-red-600"
                                title="Remove from a group"
                                onClick={() => setPendingRemoveUser(u)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            ) : null}
                            {!canEdit && !canRemove && (
                              <span className="text-xs text-zinc-400">—</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
      </div>

      <AddMemberDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        searchPool={searchPool}
        groups={coachGroups || []}
        defaultGroupId={coachGroup?.id}
      />

      <EditMemberDialog member={pendingEdit} onClose={() => setPendingEdit(null)} />

      <CoachRemoveUserDialog
        user={pendingRemoveUser}
        onCancel={() => setPendingRemoveUser(null)}
        onConfirm={handleRemoveMembership}
      />
      {showMemberStories && (
        <MemberStoryInviteDialog
          open={storyInviteOpen}
          onOpenChange={setStoryInviteOpen}
          generated={generatedStoryInvite}
          coachName={coachDisplayName}
        />
      )}
    </CoachLayout>
  )
}
